import { z } from 'zod';
import {
  Locale,
  NotificationChannel,
  NotificationEvent,
  NotificationFrequency,
  NotificationStatus,
  ParentKind,
  ScanState,
  TranslationStatus,
  Visibility,
} from './enums.js';
import { ContentLocale, IsoDateTime, LongMarkdown, Title, UserRef, Uuid, Version, boundedList } from './common.js';
import { AccessGrantInput } from './identity.js';

// ---------------------------------------------------------------- Comments

/** Fixed reaction set (rendered as emoji by the clients). */
export const CommentReactionKind = z.enum(['THUMBS_UP', 'THUMBS_DOWN', 'LAUGH', 'HOORAY', 'CONFUSED', 'HEART', 'ROCKET', 'EYES']);
export type CommentReactionKind = z.infer<typeof CommentReactionKind>;

export const CommentReactionSummary = z.object({ reaction: CommentReactionKind, count: z.number().int(), reacted: z.boolean(), people: z.array(z.string()) });
export type CommentReactionSummary = z.infer<typeof CommentReactionSummary>;

export const SetCommentReaction = z.object({ reaction: CommentReactionKind, active: z.boolean() }).strict();
export type SetCommentReaction = z.infer<typeof SetCommentReaction>;

/** Comments on requests and work items; documents use anchored threads instead (ADR-0041). */
export const CommentParentKind = z.enum(['REQUEST', 'WORK_ITEM']);
export type CommentParentKind = z.infer<typeof CommentParentKind>;

export const CommentView = z.object({
  id: Uuid,
  parentKind: CommentParentKind,
  parentId: Uuid,
  version: Version,
  contentRevision: z.number().int(),
  bodyMarkdown: z.string(),
  originalLocale: z.string(),
  author: UserRef.nullable(),
  authorKind: z.enum(['USER', 'AGENT', 'SYSTEM', 'IMPORT']),
  visibility: Visibility,
  restrictedNote: z.boolean(),
  attachments: z.array(z.object({ id: Uuid, filename: z.string(), contentType: z.string(), scanState: ScanState, byteSize: z.number().int() })),
  createdAt: IsoDateTime,
  editedAt: IsoDateTime.nullable(),
  archivedAt: IsoDateTime.nullable(),
  canEdit: z.boolean(),
  reactions: z.array(CommentReactionSummary),
});
export type CommentView = z.infer<typeof CommentView>;

export const AddComment = z
  .object({
    parentKind: CommentParentKind,
    parentId: Uuid,
    bodyMarkdown: LongMarkdown.refine((v) => v.trim().length > 0, { message: 'must not be blank' }),
    originalLocale: ContentLocale,
    visibility: Visibility.optional(),
    grants: boundedList(AccessGrantInput).optional(),
    attachmentIds: boundedList(Uuid, 10).optional(),
  })
  .strict();
export type AddComment = z.infer<typeof AddComment>;

/** On-demand AI summary of the comments the reader can see (not stored). */
export const SummarizeComments = z.object({ parentKind: CommentParentKind, parentId: Uuid }).strict();
export const ThreadSummary = z.object({ summaryMarkdown: z.string(), commentCount: z.number().int(), generatedAt: IsoDateTime, generatedBy: z.enum(['live', 'fixture']) });
export type ThreadSummary = z.infer<typeof ThreadSummary>;

export const EditComment = z
  .object({ expectedVersion: Version, bodyMarkdown: LongMarkdown.refine((v) => v.trim().length > 0) })
  .strict();
export type EditComment = z.infer<typeof EditComment>;

// ---------------------------------------------------------------- Attachments

export const CreateUploadIntent = z
  .object({
    parentKind: ParentKind,
    parentId: Uuid,
    filename: z.string().min(1).max(255),
    contentType: z.string().min(1).max(200),
    byteSize: z.number().int().min(1),
  })
  .strict();
export type CreateUploadIntent = z.infer<typeof CreateUploadIntent>;

export const AttachmentView = z.object({
  id: Uuid,
  parentKind: ParentKind,
  parentId: Uuid,
  filename: z.string(),
  contentType: z.string(),
  byteSize: z.number().int(),
  sha256: z.string().nullable(),
  scanState: ScanState,
  scanDetail: z.string().nullable(),
  uploadedBy: UserRef.nullable(),
  createdAt: IsoDateTime,
  inlinePreview: z.boolean(),
  downloadUrl: z.string().nullable(),
});
export type AttachmentView = z.infer<typeof AttachmentView>;

/**
 * Content types the API serves inline with their real type after the scan (Section 15.2); every
 * other type is a download-only octet stream. Shared by the service that enforces it, the HTTP
 * handler and the web viewer, so the browser never guesses what the server will do.
 */
export const INLINE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
export const INLINE_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'] as const;
export const INLINE_DOCUMENT_TYPES = ['application/pdf'] as const;
export const INLINE_CONTENT_TYPES: ReadonlySet<string> = new Set<string>([...INLINE_IMAGE_TYPES, ...INLINE_VIDEO_TYPES, ...INLINE_DOCUMENT_TYPES]);

export type PreviewKind = 'image' | 'video' | 'pdf';

/** How a browser can show an inline type: null for download-only content. */
export function previewKind(contentType: string): PreviewKind | null {
  if ((INLINE_IMAGE_TYPES as readonly string[]).includes(contentType)) return 'image';
  if ((INLINE_VIDEO_TYPES as readonly string[]).includes(contentType)) return 'video';
  if ((INLINE_DOCUMENT_TYPES as readonly string[]).includes(contentType)) return 'pdf';
  return null;
}

// ---------------------------------------------------------------- Translations

export const TranslatableField = z.enum([
  'title',
  'descriptionMarkdown',
  'problemMarkdown',
  'currentBehavior',
  'expectedOutcome',
  'impact',
  'acceptanceCriteria',
  'bodyMarkdown',
  'summaryMarkdown',
  'outcomeMarkdown',
]);
export type TranslatableField = z.infer<typeof TranslatableField>;

export const TranslationEntity = z.enum(['REQUEST', 'WORK_ITEM', 'COMMENT', 'PROGRESS_ENTRY']);
export type TranslationEntity = z.infer<typeof TranslationEntity>;

export const RequestTranslation = z
  .object({
    entityKind: TranslationEntity,
    entityId: Uuid,
    fields: z.array(TranslatableField).min(1).max(10),
    targetLocale: Locale,
  })
  .strict();
export type RequestTranslation = z.infer<typeof RequestTranslation>;

export const TranslationFieldResult = z.object({
  field: TranslatableField,
  status: TranslationStatus,
  sourceLocale: z.string(),
  targetLocale: Locale,
  contentRevision: z.number().int(),
  text: z.string().nullable(),
  model: z.string().nullable(),
  machineTranslated: z.boolean(),
  failureReason: z.string().nullable(),
});
export type TranslationFieldResult = z.infer<typeof TranslationFieldResult>;

export const TranslationResult = z.object({
  entityKind: TranslationEntity,
  entityId: Uuid,
  fields: z.array(TranslationFieldResult),
});
export type TranslationResult = z.infer<typeof TranslationResult>;

export const ReportTranslation = z
  .object({
    entityKind: TranslationEntity,
    entityId: Uuid,
    field: TranslatableField,
    targetLocale: Locale,
    comment: z.string().max(2000).optional(),
    regenerate: z.boolean().optional(),
  })
  .strict();
export type ReportTranslation = z.infer<typeof ReportTranslation>;

// ---------------------------------------------------------------- Notifications

export const NotificationView = z.object({
  id: Uuid,
  event: NotificationEvent,
  title: z.string(),
  body: z.string(),
  link: z.string().nullable(),
  readAt: IsoDateTime.nullable(),
  snoozedUntil: IsoDateTime.nullable(),
  archivedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type NotificationView = z.infer<typeof NotificationView>;

/** Inbox views: inbox (default), mentions, unread, snoozed and archived. */
export const NotificationInboxView = z.enum(['inbox', 'mentions', 'unread', 'snoozed', 'archived']);
export type NotificationInboxView = z.infer<typeof NotificationInboxView>;

/** Archive or snooze in-app notifications (own only). `allRead` archives every read inbox item. */
export const UpdateNotifications = z
  .object({
    action: z.enum(['archive', 'unarchive', 'snooze', 'unsnooze']),
    ids: boundedList(Uuid, 100).optional(),
    allRead: z.boolean().optional(),
    until: IsoDateTime.optional(),
  })
  .strict()
  .refine((v) => (v.ids?.length ?? 0) > 0 || (v.allRead === true && v.action === 'archive'), { message: 'Give ids, or allRead with archive' })
  .refine((v) => v.action !== 'snooze' || !!v.until, { message: 'Snoozing needs until', path: ['until'] });
export type UpdateNotifications = z.infer<typeof UpdateNotifications>;

export const NotificationPreference = z.object({
  channel: NotificationChannel,
  event: NotificationEvent,
  frequency: NotificationFrequency,
});
export type NotificationPreference = z.infer<typeof NotificationPreference>;

export const SetNotificationPreferences = z.object({ preferences: boundedList(NotificationPreference, 60) }).strict();

/** A browser's Web Push subscription (PushSubscription.toJSON()). Push services use https endpoints. */
export const RegisterPushSubscription = z
  .object({
    endpoint: z.url({ protocol: /^https$/ }).max(2000),
    keys: z.object({ p256dh: z.string().regex(/^[A-Za-z0-9_-]+={0,2}$/).max(200), auth: z.string().regex(/^[A-Za-z0-9_-]+={0,2}$/).max(100) }).strict(),
    /** Short device description shown in the profile, such as "Chrome on Windows". */
    label: z.string().trim().max(120).optional(),
  })
  .strict();
export type RegisterPushSubscription = z.infer<typeof RegisterPushSubscription>;

export const UnregisterPushSubscription = z.object({ endpoint: z.url({ protocol: /^https$/ }).max(2000) }).strict();

export const PushDeviceView = z.object({
  id: Uuid,
  label: z.string().nullable(),
  createdAt: IsoDateTime,
  lastSuccessAt: IsoDateTime.nullable(),
  lastFailureAt: IsoDateTime.nullable(),
});
export type PushDeviceView = z.infer<typeof PushDeviceView>;

export const DeliveryDiagnosticView = z.object({
  id: Uuid,
  channel: NotificationChannel,
  event: NotificationEvent,
  recipient: UserRef.nullable(),
  status: NotificationStatus,
  attempts: z.number().int(),
  providerMessageId: z.string().nullable(),
  lastSafeError: z.string().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type DeliveryDiagnosticView = z.infer<typeof DeliveryDiagnosticView>;

// ---------------------------------------------------------------- Intake assistant

export const IntakeTurn = z
  .object({
    draftId: Uuid.optional(),
    expectedVersion: Version.optional(),
    message: z.string().min(1).max(8000),
    locale: Locale,
  })
  .strict();
export type IntakeTurn = z.infer<typeof IntakeTurn>;

export const IntakeTurnResult = z.object({
  draftId: Uuid,
  draftVersion: Version,
  assistantMessage: z.string(),
  missingFields: z.array(z.string()),
  readyForReview: z.boolean(),
  aiAvailable: z.boolean(),
  generatedBy: z.enum(['fixture', 'live', 'rules']),
});
export type IntakeTurnResult = z.infer<typeof IntakeTurnResult>;

// ---------------------------------------------------------------- Saved views

export const SavedViewScope = z.enum(['WORK', 'REQUESTS', 'REPORT']);
export const SavedView = z.object({
  id: Uuid,
  name: z.string(),
  scope: SavedViewScope,
  query: z.record(z.string(), z.string()),
  shared: z.boolean(),
  owner: UserRef,
  createdAt: IsoDateTime,
});
export type SavedView = z.infer<typeof SavedView>;

export const CreateSavedView = z
  .object({
    name: Title,
    scope: SavedViewScope,
    query: z.record(z.string().max(40), z.string().max(300)),
    shared: z.boolean().optional(),
  })
  .strict();
export type CreateSavedView = z.infer<typeof CreateSavedView>;

// ---------------------------------------------------------------- Jobs

export const JobView = z.object({
  id: Uuid,
  kind: z.string(),
  status: z.enum(['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED', 'CANCELED']),
  progress: z.number().min(0).max(1),
  resultUrl: z.string().nullable(),
  safeError: z.string().nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type JobView = z.infer<typeof JobView>;
